const BRIDGE_NAME = 'SafePhoneBridge'
const WAIT_MS = 3000
const POLL_MS = 100

export const hasNativeTransport = () => {
  if (typeof window === 'undefined') return false
  const injected = window[BRIDGE_NAME]
  if (injected && (typeof injected.postMessage === 'function' || 'postMessage' in injected)) {
    return true
  }
  const handler = window.webkit?.messageHandlers?.[BRIDGE_NAME]
  return !!(handler && typeof handler.postMessage === 'function')
}

export const waitForNativeTransport = (timeoutMs = WAIT_MS) => {
  if (hasNativeTransport()) return Promise.resolve(true)
  const started = Date.now()
  return new Promise((resolve) => {
    const timer = setInterval(() => {
      if (hasNativeTransport()) {
        clearInterval(timer)
        resolve(true)
        return
      }
      if (Date.now() - started >= timeoutMs) {
        clearInterval(timer)
        resolve(hasNativeTransport())
      }
    }, POLL_MS)
  })
}

const scriptSrc = () => {
  const base = useRuntimeConfig().app.baseURL || '/view'
  return `${base.replace(/\/$/, '')}/assets/pk.js`
}

const exposePkOnWindow = () => {
  // pk.js 는 const pk 라 window.pk 가 아니다. 클래식 스크립트에서만 같은 pk 를 볼 수 있다.
  if (window.pk) return
  const expose = document.createElement('script')
  expose.textContent = 'window.pk = pk;'
  document.head.appendChild(expose)
  expose.remove()
}

const injectPkScript = () =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-pk-sdk]')
    if (existing) existing.remove()
    const script = document.createElement('script')
    script.src = scriptSrc()
    script.dataset.pkSdk = 'true'
    script.onload = () => {
      exposePkOnWindow()
      resolve()
    }
    script.onerror = () => reject(new Error('pk.js 로드 실패'))
    document.head.appendChild(script)
  })

// 진행 중인 준비 작업을 공유한다. 플러그인이 미리 깨우고 화면이 동시에 호출해도
// pk.js 가 두 번 주입되지 않는다. 끝나면 비워서, 브릿지가 늦게 올라온 경우의
// 복구(resolvePk)가 다시 동작할 수 있게 둔다.
let pending = null

const sharePk = () => {
  if (pending) return pending
  pending = resolvePk().finally(() => {
    pending = null
  })
  return pending
}

export const ensurePk = async () => {
  if (typeof window === 'undefined') return null

  // 앱이 아니면 pk.js 를 아예 받지 않는다. 브릿지 대기도 하지 않으므로 즉시 끝난다.
  // (마커 미설정 구간에서는 shouldLoadPk 가 항상 true 라 기존 동작이 유지된다)
  if (!shouldLoadPk()) return null

  const pk = await sharePk()

  // 앱이 켜질 때 플러그인이 띄워 둔 준비 작업이 브릿지보다 먼저 끝나면 available 이 false 로 굳는다.
  // 그 대기 구간(최대 3초)에 사용자가 버튼을 누르면 pending 을 공유해, 실패가 예정된 작업을 그대로 받았다.
  // 아래 resolvePk 의 복구는 '다음 호출'에만 동작하므로 그 첫 탭만 혼자 실패했고, 다시 누르면 됐다.
  // 브릿지가 그사이 올라와 있다면 여기서 한 번 더 준비해서 그 첫 탭도 살린다.
  if (pk && !pk.available && hasNativeTransport()) {
    return sharePk()
  }
  return pk
}

// 브릿지 없음을 한 번 확정했으면 다시 기다리지 않는다.
// 대기는 웹뷰가 브릿지를 늦게 붙이는 경우를 위한 것이라, 한 번 끝냈다면 목적을 다했다.
// 브라우저에서 isNativeApp() 을 부를 때마다 3초씩 다시 기다리던 문제를 없앤다.
// 브릿지가 뒤늦게 나타나면 아래 복구 로직이 받아서 pk.js 를 다시 주입한다.
let transportSettled = false

const resolvePk = async () => {
  if (!transportSettled) {
    await waitForNativeTransport()
    transportSettled = true
  }

  // pk.js는 로드 시점에 전송 계층을 한 번만 본다. 브릿지보다 먼저 돌면 available이 영구 false다.
  if (window.pk && !window.pk.available && hasNativeTransport()) {
    window.pk = undefined
  }

  if (!window.pk) {
    await injectPkScript()
  }

  if (window.pk?.ready) {
    await window.pk.ready
  }
  return window.pk || null
}
